import api from '../lib/axios';

/**
 * Response from upload file API
 */
export interface UploadFileResponse {
    url: string;
}

/**
 * Upload a file to the server
 * 
 * API Endpoint: POST /v1/portal/utility/upload-file
 * Content-Type: multipart/form-data
 * 
 * @param file - The file to upload
 * @returns Promise<string> - The URL of the uploaded file
 * @throws Error if upload fails
 */
export const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<UploadFileResponse>(
        '/v1/portal/utility/upload-file',
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    if (!response.data?.url) {
        throw new Error('Upload failed: No URL returned');
    }

    return response.data.url;
};

/**
 * Upload an image file with validation
 * 
 * @param file - The image file to upload
 * @param maxSizeMB - Maximum file size in MB (default: 10MB)
 * @returns Promise<string> - The URL of the uploaded image
 * @throws Error if validation or upload fails
 */
export const uploadImage = async (file: File, maxSizeMB: number = 10): Promise<string> => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
        throw new Error('INVALID_FILE_TYPE');
    }

    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        throw new Error('FILE_TOO_LARGE');
    }

    return uploadFile(file);
};

